import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Bot,
  Briefcase,
  CalendarClock,
  Check,
  ChevronRight,
  Clock,
  Inbox,
  Layers3,
  Mic,
  Plus,
  RefreshCw,
  Send,
  ShieldAlert,
  Sparkles,
  Target,
  Trophy,
  X,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/authStore";
import { useToasts } from "../components/Toasts";
import { displayBusinessName, displayProductText as displayText } from "../lib/productNames";
import "./FocusCommandPage.css";

const BUCKETS = ["NOW", "NEXT", "PARKING_LOT"];
const BUCKET_LABEL = { NOW: "Now", NEXT: "Next", PARKING_LOT: "Parking Lot" };
const ROLES = ["Founder", "Family", "Employee", "Personal"];
const PARTNER_KEY = "focuslinc.partner.history.v1";

const QUICK_PROMPTS = [
  "What needs me most right now?",
  "What am I missing?",
  "What can AI handle without me?",
  "Where am I blocked or waiting?",
];

function labelBusiness(businesses, key) {
  const business = businesses.find((b) => (b.key || b.slug) === key);
  return business ? displayBusinessName(business) : displayText(key);
}

function CommandCard({ title, note, value, Icon, tone, onClick, wide = false, status }) {
  return (
    <button className={`fc-command-card tone-${tone}${wide ? " wide" : ""}`} onClick={onClick}>
      <span className="fc-command-icon"><Icon size={18} /></span>
      <span className="fc-command-copy">
        <span className="fc-command-title"><b>{title}</b>{status ? <small className="fc-status-dot">{status}</small> : null}</span>
        <small>{displayText(note)}</small>
      </span>
      {value !== undefined && value !== null ? <strong className="fc-command-value">{value}</strong> : null}
      <ChevronRight className="fc-command-chevron" size={16} />
    </button>
  );
}

function Overlay({ title, eyebrow = "FOCUSLINC", tone = "blue", onClose, onBack, children, actions }) {
  useEffect(() => {
    const key = (event) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onClose]);

  return (
    <div className="fc-overlay" onMouseDown={onClose} role="presentation">
      <section className={`fc-overlay-card tone-${tone}`} onMouseDown={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-label={title} data-edit-name={title ? `FocusLinc · ${title}` : "FocusLinc overlay"}>
        <header className="fc-overlay-head">
          <div className="fc-overlay-title">
            {onBack ? <button className="fc-overlay-icon" onClick={onBack} title="Back"><ArrowLeft size={17} /></button> : null}
            <div><small>{eyebrow}</small><h2>{displayText(title)}</h2></div>
          </div>
          <div className="fc-overlay-actions">
            {actions}
            <button className="fc-overlay-icon" onClick={onClose} title="Close"><X size={18} /></button>
          </div>
        </header>
        <div className="fc-overlay-body">{children}</div>
      </section>
    </div>
  );
}

function RowButton({ title, meta, badge, tone = "slate", onClick, children }) {
  return (
    <button className={`fc-row-button tone-${tone}`} onClick={onClick}>
      <span><b>{displayText(title)}</b>{meta ? <small>{displayText(meta)}</small> : null}</span>
      {badge ? <em>{badge}</em> : null}
      {children}
      <ChevronRight size={15} />
    </button>
  );
}

function DetailPanel({ detail, businesses, onNavigate, onDone }) {
  if (!detail) return null;
  const item = detail.item || detail;
  const route = detail.route || item.route || (item.mission_id ? `/commandlinc/missions/${item.mission_id}` : null);
  const business = item.business_key ? labelBusiness(businesses, item.business_key) : null;
  return (
    <div className="fc-detail">
      <div className="fc-detail-hero">
        <span className="fc-detail-kind">{detail.kind || item.kind || detail.type || "item"}</span>
        <h3>{displayText(detail.title || item.title || item.name)}</h3>
        {detail.meta ? <p>{displayText(detail.meta)}</p> : null}
      </div>
      <div className="fc-detail-grid">
        {item.bucket ? <span><small>Bucket</small><b>{BUCKET_LABEL[item.bucket] || item.bucket}</b></span> : null}
        {item.role ? <span><small>Role</small><b>{item.role}</b></span> : null}
        {business ? <span><small>Business</small><b>{business}</b></span> : null}
        {item.urgency_score !== undefined ? <span><small>Urgency</small><b>{item.urgency_score}</b></span> : null}
        {item.risk_score !== undefined ? <span><small>Risk</small><b>{item.risk_score}</b></span> : null}
        {item.status ? <span><small>Status</small><b>{item.status}</b></span> : null}
        {item.stage || item.current_stage ? <span><small>Stage</small><b>{item.stage || item.current_stage}</b></span> : null}
        {item.target_date ? <span><small>Target</small><b>{item.target_date}</b></span> : null}
      </div>
      {item.notes ? <p className="fc-detail-notes">{displayText(item.notes)}</p> : null}
      <div className="fc-detail-actions">
        {item.id && item.bucket && onDone ? <button className="btn primary" onClick={() => onDone(item)}><Check size={14} /> Done</button> : null}
        {route ? <button className="btn" onClick={() => onNavigate(route)}>Open source <ArrowRight size={14} /></button> : null}
      </div>
    </div>
  );
}

function CapturePanel({ businesses, onCreate }) {
  const [title, setTitle] = useState("");
  const [bucket, setBucket] = useState("NOW");
  const [kind, setKind] = useState("general");
  const [role, setRole] = useState("Founder");
  const [businessKey, setBusinessKey] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (!title.trim() || busy) return;
    setBusy(true);
    try {
      await onCreate({ title: title.trim(), bucket, kind, role: role || null, business_key: businessKey || null, notes: notes || null });
    } finally { setBusy(false); }
  };

  return (
    <form className="fc-capture" onSubmit={submit}>
      <label><small>What do you need to remember?</small><input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Quick capture…" /></label>
      <div className="fc-capture-grid">
        <label><small>Bucket</small><select value={bucket} onChange={(e) => setBucket(e.target.value)}>{BUCKETS.map((b) => <option key={b} value={b}>{BUCKET_LABEL[b]}</option>)}</select></label>
        <label><small>Type</small><select value={kind} onChange={(e) => setKind(e.target.value)}><option value="general">General</option><option value="needs_you">Needs You</option><option value="waiting_on">Waiting On</option><option value="at_risk">At Risk</option><option value="ai_can_handle">AI Can Handle</option><option value="upcoming">Upcoming</option></select></label>
        <label><small>Role</small><select value={role} onChange={(e) => setRole(e.target.value)}><option value="">None</option>{ROLES.map((r) => <option key={r}>{r}</option>)}</select></label>
        <label><small>Business</small><select value={businessKey} onChange={(e) => setBusinessKey(e.target.value)}><option value="">None</option>{businesses.map((b) => <option key={b.key || b.slug} value={b.key || b.slug}>{displayBusinessName(b)}</option>)}</select></label>
      </div>
      <label><small>Notes</small><textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
      <div className="fc-capture-actions"><button className="btn primary" disabled={!title.trim() || busy}>{busy ? "Saving…" : "Capture"}</button></div>
    </form>
  );
}

function PartnerPanel({ onNavigate }) {
  const [history, setHistory] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(PARTNER_KEY) || "[]");
      if (Array.isArray(saved) && saved.length) return saved;
    } catch { /* ignore malformed local history */ }
    return [{ role: "assistant", content: "I’m here. Ask what needs you, what is drifting, or what I can take off your plate." }];
  });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/focus/partner")
      .then(({ data }) => {
        if (!alive || !Array.isArray(data?.messages) || !data.messages.length) return;
        setHistory(data.messages);
      })
      .catch(() => { /* local history remains a safe fallback */ });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    try { localStorage.setItem(PARTNER_KEY, JSON.stringify(history.slice(-30))); } catch { /* storage unavailable */ }
  }, [history]);

  const send = async (text) => {
    const message = text.trim();
    if (!message || busy) return;
    const userTurn = { role: "user", content: message };
    const prior = history.slice(-10).map(({ role, content }) => ({ role, content }));
    setHistory((current) => [...current, userTurn]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/focus/partner", { message, history: prior });
      setHistory((current) => [...current, { role: "assistant", content: data.reply, attention_callout: data.attention_callout, actions: data.suggested_actions || [], provider: data.provider, model: data.model }]);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setHistory((current) => [...current, { role: "assistant", content: typeof detail === "string" ? detail : "I couldn’t reach the FocusLinc partner service on that turn." }]);
    } finally { setBusy(false); }
  };

  const dictate = () => {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) return;
    const recognition = new Recognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.onstart = () => setListening(true);
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript || "";
      setInput((current) => current ? `${current} ${transcript}` : transcript);
    };
    recognition.start();
  };

  return (
    <div className="fc-partner">
      <div className="fc-partner-prompts">{QUICK_PROMPTS.map((prompt) => <button key={prompt} onClick={() => send(prompt)} disabled={busy}>{prompt}</button>)}</div>
      <div className="fc-partner-messages">
        {history.map((turn, index) => (
          <article className={`fc-partner-message ${turn.role}`} key={`${turn.role}-${index}`}>
            <small>{turn.role === "assistant" ? "LINCWERKS" : "YOU"}</small>
            <p>{displayText(turn.content)}</p>
            {turn.attention_callout ? <div className="fc-partner-callout"><Target size={13} /> {displayText(turn.attention_callout)}</div> : null}
            {turn.actions?.length ? <div className="fc-partner-actions">{turn.actions.map((action, i) => (
              <button key={`${action.label}-${i}`} onClick={() => action.route && onNavigate(action.route)} disabled={!action.route}>{displayText(action.label)}</button>
            ))}</div> : null}
          </article>
        ))}
        {busy ? <article className="fc-partner-message assistant thinking"><small>LINCWERKS</small><p>Working through the live FocusLinc state…</p></article> : null}
      </div>
      <form className="fc-partner-composer" onSubmit={(e) => { e.preventDefault(); send(input); }}>
        <button type="button" className={`fc-mic${listening ? " active" : ""}`} onClick={dictate} title="Dictate"><Mic size={17} /></button>
        <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={2} placeholder="Talk or type to your LincWerks partner…" onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }} />
        <button className="fc-send" disabled={!input.trim() || busy} title="Send"><Send size={17} /></button>
      </form>
    </div>
  );
}

export default function FocusCommandPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToasts((state) => state.push);
  const [summary, setSummary] = useState(null);
  const [top3, setTop3] = useState(null);
  const [items, setItems] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [overlay, setOverlay] = useState(null);
  const [detail, setDetail] = useState(null);
  const [planningBucket, setPlanningBucket] = useState("NOW");
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    const [{ data: s }, { data: ranking }, { data: focusItems }, { data: biz }] = await Promise.all([
      api.get("/focus/summary"), api.get("/focus/top3"), api.get("/focus_items?status=open&limit=300"), api.get("/businesses"),
    ]);
    setSummary(s); setTop3(ranking); setItems(Array.isArray(focusItems) ? focusItems : []); setBusinesses(Array.isArray(biz) ? biz : []);
  }, []);

  useEffect(() => { load().catch(() => toast("FocusLinc could not refresh.", "err")); }, [load, toast]);

  const manualKinds = useMemo(() => ({
    needs_you: items.filter((i) => i.kind === "needs_you"), waiting_on: items.filter((i) => i.kind === "waiting_on"), at_risk: items.filter((i) => i.kind === "at_risk"), ai_can_handle: items.filter((i) => i.kind === "ai_can_handle"),
  }), [items]);

  const needsRows = useMemo(() => [
    ...(summary?.needs_you || []).map((a) => ({ type: "Approval", title: a.mission_title || "Approval required", meta: `${a.stage_key || "stage"} · ${a.kind || "decision"}`, route: `/commandlinc/missions/${a.mission_id}` })),
    ...manualKinds.needs_you.map((item) => ({ type: "Focus item", title: item.title, meta: item.business_key || item.role || "", item })),
  ], [summary, manualKinds]);

  const waitingRows = useMemo(() => [
    ...(summary?.waiting_on || []).map((w) => ({ type: "Waiting", title: w.title || "Waiting item", meta: w.detail || "" })),
    ...manualKinds.waiting_on.map((item) => ({ type: "Focus item", title: item.title, meta: item.business_key || item.role || "", item })),
  ], [summary, manualKinds]);

  const riskRows = useMemo(() => [
    ...(summary?.at_risk?.businesses || []).map((b) => ({ type: "Business", title: displayBusinessName(b), meta: `${b.health}${b.target_date ? ` · target ${b.target_date}` : ""}`, route: `/businesses/${b.key}`, ...b })),
    ...(summary?.at_risk?.missions || []).map((m) => ({ type: "Mission", title: m.title, meta: `stage ${m.current_stage}`, route: `/commandlinc/missions/${m.id}`, ...m })),
    ...manualKinds.at_risk.map((item) => ({ type: "Focus item", title: item.title, meta: item.business_key || item.role || "", item })),
  ], [summary, manualKinds]);

  const aiRows = useMemo(() => [
    ...(summary?.ai_can_handle || []).map((m) => ({ type: "Mission", title: m.title, meta: `stage ${m.current_stage}`, route: `/commandlinc/missions/${m.id}`, ...m })),
    ...manualKinds.ai_can_handle.map((item) => ({ type: "Focus item", title: item.title, meta: item.business_key || item.role || "", item })),
  ], [summary, manualKinds]);

  const topRows = top3?.top_3 || [];
  const visibleBusinesses = businesses.filter((b) => b.visible !== false && !b.self_managed);
  const upcoming = summary?.upcoming || [];
  const closeOverlay = useCallback(() => { setOverlay(null); setDetail(null); }, []);
  const show = (name) => { setDetail(null); setOverlay(name); };

  const sync = async () => { setSyncing(true); try { await api.post("/focus/sync"); await load(); toast("FocusLinc synced.", "ok"); } catch { toast("Sync failed.", "err"); } finally { setSyncing(false); } };
  const createItem = async (payload) => { await api.post("/focus_items", payload); toast("Captured.", "ok"); closeOverlay(); await load(); };
  const doneItem = async (item) => { await api.post(`/focus_items/${item.id}/status`, { status: "done" }); toast("Done.", "ok"); setDetail(null); await load(); };
  const navigateFromOverlay = (route) => { if (!route) return; closeOverlay(); navigate(route); };
  const openRow = (row) => setDetail(row);
  const questionData = {
    needs: { title: "What needs me now?", rows: needsRows, tone: "amber" },
    waiting: { title: "What am I waiting for?", rows: waitingRows, tone: "cyan" },
    risk: { title: "What’s at risk?", rows: riskRows, tone: "red" },
    ai: { title: "What can AI handle?", rows: aiRows, tone: "purple" },
  };

  const renderRows = (rows, empty) => rows.length ? (
    <div className="fc-overlay-list">{rows.map((row, index) => <RowButton key={`${row.title}-${index}`} title={row.title} meta={row.meta} tone={row.type === "Business" ? "teal" : row.type === "Mission" ? "purple" : "blue"} badge={row.type} onClick={() => openRow(row)} />)}</div>
  ) : <div className="fc-empty"><Check size={19} /><b>{empty}</b><small>Nothing is demanding space here right now.</small></div>;

  return (
    <div className="focus-command-page" data-testid="focus-command-page" data-edit-name="FocusLinc command">
      <header className="fc-header">
        <div className="fc-brand-block">
          <img src="/LincWerks2026Logo.png" alt="LincWerks" />
          <div><small>LINCWERKS · HOME</small><h1>Focus<span>Linc</span></h1><p>Know what matters. Talk it through. Move.</p></div>
        </div>
        <div className="fc-header-actions"><span className="fc-user">{user?.name || "Director"}</span><button className="fc-icon-button" onClick={sync} disabled={syncing} title="Sync attention"><RefreshCw className={syncing ? "spin" : ""} size={17} /></button><button className="fc-capture-button" onClick={() => show("capture")}><Plus size={15} /> Capture</button></div>
      </header>

      <section className="fc-question-grid" aria-label="FocusLinc questions" data-edit-name="FocusLinc questions">
        <CommandCard title="What needs me now?" note={needsRows[0]?.title || "No decisions waiting"} value={needsRows.length} Icon={Inbox} tone="amber" onClick={() => show("needs")} />
        <CommandCard title="What am I waiting for?" note={waitingRows[0]?.title || "Comms feed phases in next"} value={waitingRows.length} Icon={Clock} tone="cyan" onClick={() => show("waiting")} />
        <CommandCard title="What’s at risk?" note={riskRows[0]?.title || "Nothing currently flagged"} value={riskRows.length} Icon={ShieldAlert} tone="red" onClick={() => show("risk")} />
        <CommandCard title="What can AI handle?" note={aiRows[0]?.title || "Ask the partner to find work"} value={aiRows.length} Icon={Sparkles} tone="purple" onClick={() => show("ai")} />
        <CommandCard title="AI Partner" note="Talk to the live FocusLinc state" Icon={Bot} tone="violet" status={summary?.system_health?.llm_provider_reachable ? "online" : "setup"} onClick={() => show("partner")} />
      </section>

      <section className="fc-top3-panel" data-edit-name="Today's Top 3"><div className="fc-section-head"><span><Trophy size={15} /> TODAY’S TOP 3</span><small>Attention Engine · ranked, not just listed</small></div><div className="fc-top3-grid">{topRows.length ? topRows.map((ranked, index) => <button className={`fc-top3-card rank-${index + 1}`} key={ranked.item?.id || index} onClick={() => { setOverlay("top3"); setDetail({ type: "Top priority", title: ranked.item?.title, meta: (ranked.reasons || []).join(" · "), item: ranked.item }); }}><span className="fc-rank">{index + 1}</span><span><b>{displayText(ranked.item?.title || "Untitled")}</b><small>{displayText((ranked.reasons || [])[0] || "In your attention queue")}</small></span><em>{Math.round(ranked.score || 0)}</em></button>) : <div className="fc-top3-empty">No ranked items yet. Capture something important or sync FocusLinc.</div>}</div></section>

      <section className="fc-utility-grid">
        <CommandCard title="Planning" note={`${items.filter((i) => i.bucket === "NOW").length} now · ${items.filter((i) => i.bucket === "NEXT").length} next`} Icon={Layers3} tone="blue" onClick={() => show("planning")} />
        <CommandCard title="Quick Capture" note="Get it out of your head" Icon={Plus} tone="green" onClick={() => show("capture")} />
        <CommandCard title="Businesses" note={`${visibleBusinesses.length} active surfaces`} Icon={Briefcase} tone="teal" onClick={() => show("businesses")} />
        <CommandCard title="Upcoming" note={upcoming[0]?.name || "No dated milestone in range"} value={upcoming.length} Icon={CalendarClock} tone="violet" onClick={() => show("upcoming")} />
      </section>

      {overlay === "partner" ? <Overlay title="AI Partner" eyebrow="LINCWERKS PARTNER" tone="violet" onClose={closeOverlay}><PartnerPanel onNavigate={navigateFromOverlay} /></Overlay> : null}
      {overlay === "capture" ? <Overlay title="Quick Capture" tone="green" onClose={closeOverlay}><CapturePanel businesses={visibleBusinesses} onCreate={createItem} /></Overlay> : null}
      {questionData[overlay] ? <Overlay title={questionData[overlay].title} tone={questionData[overlay].tone} onClose={closeOverlay} onBack={detail ? () => setDetail(null) : null}>{detail ? <DetailPanel detail={detail} businesses={businesses} onNavigate={navigateFromOverlay} onDone={doneItem} /> : renderRows(questionData[overlay].rows, "Clear")}</Overlay> : null}
      {overlay === "top3" ? <Overlay title="Today’s Top 3" tone="cyan" onClose={closeOverlay} onBack={detail ? () => setDetail(null) : null}>{detail ? <DetailPanel detail={detail} businesses={businesses} onNavigate={navigateFromOverlay} onDone={doneItem} /> : renderRows(topRows.map((r) => ({ type: "Top priority", title: r.item?.title, meta: (r.reasons || []).join(" · "), item: r.item })), "No priorities")}</Overlay> : null}
      {overlay === "planning" ? <Overlay title="Planning" tone="blue" onClose={closeOverlay} onBack={detail ? () => setDetail(null) : null} actions={!detail ? <button className="fc-overlay-small-action" onClick={() => show("capture")}><Plus size={13} /> Capture</button> : null}>{detail ? <DetailPanel detail={detail} businesses={businesses} onNavigate={navigateFromOverlay} onDone={doneItem} /> : <div className="fc-planning"><div className="fc-bucket-tabs">{BUCKETS.map((bucket) => <button key={bucket} className={planningBucket === bucket ? "active" : ""} onClick={() => setPlanningBucket(bucket)}>{BUCKET_LABEL[bucket]} <em>{items.filter((i) => i.bucket === bucket).length}</em></button>)}</div>{renderRows(items.filter((i) => i.bucket === planningBucket).map((item) => ({ type: "Focus item", title: item.title, meta: `${item.kind.replace(/_/g, " ")}${item.role ? ` · ${item.role}` : ""}`, item })), "Bucket clear")}</div>}</Overlay> : null}
      {overlay === "businesses" ? <Overlay title="Businesses" tone="teal" onClose={closeOverlay} onBack={detail ? () => setDetail(null) : null}>{detail ? <DetailPanel detail={detail} businesses={businesses} onNavigate={navigateFromOverlay} /> : renderRows(visibleBusinesses.map((b) => ({ type: "Business", title: displayBusinessName(b), meta: `${b.status || ""}${b.health ? ` · ${b.health}` : ""}`, route: `/businesses/${b.key || b.slug}`, ...b })), "No businesses")}</Overlay> : null}
      {overlay === "upcoming" ? <Overlay title="Upcoming" tone="violet" onClose={closeOverlay} onBack={detail ? () => setDetail(null) : null}>{detail ? <DetailPanel detail={detail} businesses={businesses} onNavigate={navigateFromOverlay} /> : renderRows(upcoming.map((u) => ({ type: "Milestone", title: displayText(u.name), meta: `${u.target_date || ""}${u.current_milestone ? ` · ${displayText(u.current_milestone)}` : ""}`, route: `/businesses/${u.key}`, ...u })), "Nothing upcoming")}</Overlay> : null}
    </div>
  );
}
